/* Journal Services — each service as a Roman-numeral chapter entry.
   Wordy considered serif body. Price + reserve link in italic. */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useTenantTheme } from "@/hooks/useTenantTheme";
import { tenantHref } from "@/lib/tenant";

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];

function startingPrice(s) {
  const tiers = Array.isArray(s?.tiers) ? s.tiers : [];
  const tp = tiers.map((t) => Number(t?.price)).filter((n) => Number.isFinite(n) && n > 0);
  if (tp.length) return Math.min(...tp);
  const n = Number(s?.price_from ?? s?.starting_price ?? s?.price);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export default function JournalServices() {
  useTenantTheme();
  const [services, setServices] = useState([]);
  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data || [])).catch(() => {});
  }, []);

  return (
    <div
      className="min-h-screen"
      style={{ background: "#F5F2EC", color: "#0E0D0C", fontFamily: "var(--tenant-serif)" }}
      data-testid="journal-services"
    >
      <section className="pt-16 pb-12 px-6 lg:px-12 max-w-4xl mx-auto text-center">
        <p className="text-[10px] uppercase tracking-[0.42em] text-[color:var(--tenant-accent)] mb-4">
          Engagement · The Index
        </p>
        <h1
          className="font-light italic leading-[1.0]"
          style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(3rem, 8vw, 6rem)" }}
        >
          Services
        </h1>
      </section>

      <div className="max-w-3xl mx-auto px-6 lg:px-12 pb-24">
        {services.length === 0 ? (
          <p className="text-center italic text-[color:var(--tenant-fg,#0E0D0C)]/55 py-32">The index is being typeset.</p>
        ) : (
          services.map((s, i) => {
            const p = startingPrice(s);
            return (
              <article
                key={s.id || s.slug}
                className="py-12"
                style={{ borderBottom: i === services.length - 1 ? "none" : "1px solid var(--tenant-accent-line, rgba(201,169,97,0.35))" }}
                data-testid={`journal-service-${s.slug}`}
              >
                <p className="text-[10px] uppercase tracking-[0.42em] text-[color:var(--tenant-accent)] mb-3">
                  Chapter {ROMAN[i] || (i + 1)}
                </p>
                <h2 className="text-3xl sm:text-4xl italic font-light leading-tight mb-5" style={{ fontFamily: "var(--tenant-serif)" }}>
                  {s.name}
                </h2>
                {(s.description || s.short_description) && (
                  <p className="text-base sm:text-lg leading-[1.85] text-[color:var(--tenant-fg,#0E0D0C)]/80 max-w-2xl" style={{ fontFamily: "var(--tenant-serif)" }}>
                    {s.description || s.short_description}
                  </p>
                )}
                <div className="mt-6 flex items-baseline justify-between flex-wrap gap-4">
                  <span className="italic text-xl text-[color:var(--tenant-fg,#0E0D0C)]/75" style={{ fontFamily: "var(--tenant-serif)" }}>
                    {p != null ? `from $${p}` : "by inquiry"}
                  </span>
                  <Link
                    to={tenantHref(`/booking?service=${s.slug}`)}
                    className="italic text-base text-[color:var(--tenant-fg,#0E0D0C)] hover:opacity-70 transition-opacity"
                    style={{ fontFamily: "var(--tenant-serif)", borderBottom: "1px solid var(--tenant-accent, #C9A961)" }}
                  >
                    Reserve →
                  </Link>
                </div>
              </article>
            );
          })
        )}
      </div>
    </div>
  );
}
